import styled, { css, keyframes } from "styled-components";
import { typographyCSS } from "@ifgengineering/design-tokens/legacy";
import { branch } from "@styles/branchColour";

/** A desktop window too short for the roomy spacing, so a step still fits without scrolling. */
export const onShortDesktop =
  "@media (min-width: 1024px) and (max-height: 900px)";

export const StepForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space["4xl"]}px;

  ${onShortDesktop} {
    gap: ${({ theme }) => theme.space.section.gap}px;
  }
`;

export const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md}px;
`;

const focusRing = css`
  &:focus-visible {
    outline: ${({ theme }) => theme.stroke.focus}px solid
      ${({ theme }) => branch(theme, "border/focus")};
    outline-offset: ${({ theme }) => theme.stroke.focus}px;
  }
`;

export const StepHeading = styled.h1`
  ${typographyCSS("heading.xl")}
  margin: 0;

  @media (min-width: 768px) {
    ${typographyCSS("number.xl")}
    font-weight: 600;
    line-height: 44px;
  }

  ${onShortDesktop} {
    ${typographyCSS("heading.xl")}
  }

  color: ${({ theme }) => branch(theme, "text/primary")};
  letter-spacing: -0.02em;
  text-wrap: balance;

  &:focus {
    outline: none;
  }

  ${focusRing}
`;

export const StepOptional = styled.span`
  ${typographyCSS("body.md")}
  margin-left: ${({ theme }) => theme.space.sm}px;
  color: ${({ theme }) => branch(theme, "text/tertiary")};
`;

export const StepSubheading = styled.div`
  ${typographyCSS("body.md")}
  max-width: 62ch;
  color: ${({ theme }) => branch(theme, "text/secondary")};

  & p {
    margin: 0;
  }
`;

export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.section.gap}px;
`;

/** The action bar under a step: Back and the primary action, then any reassurance. */
export const stepFooterCss = css`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.card.gap}px;
`;

/** Keeps the actions at the bottom of the window, so a step taller than it never pushes them away. */
export const pinnedFooterCss = css`
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.raised};
  padding: ${({ theme }) => theme.space["2xs"]}px 0
    ${({ theme }) => theme.space.md}px;
  background: ${({ theme }) => branch(theme, "background/page")};
`;

export const StepFooter = styled.div`
  ${stepFooterCss}
  ${pinnedFooterCss}
`;

/** Back on the left, the primary action on the right, whichever of them is shown. */
export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.space.card.gap}px;
`;

const actionBase = css`
  ${typographyCSS("label.md")}
  box-sizing: border-box;
  height: ${({ theme }) => theme.size.control.md}px;
  border-radius: ${({ theme }) => theme.radius.full}px;
  border: ${({ theme }) => theme.stroke.md}px solid;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm}px;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.duration.quick}ms
    ${({ theme }) => theme.motion.easing.standard};
  ${focusRing}

  &:disabled {
    cursor: not-allowed;
  }
`;

export const PrimaryAction = styled.button`
  ${actionBase}
  flex: 1;
  padding: 0 ${({ theme }) => theme.space["2xl"]}px;
  border-color: ${({ theme }) => branch(theme, "action/primary/background")};
  background: ${({ theme }) => branch(theme, "action/primary/background")};
  color: ${({ theme }) => branch(theme, "action/primary/text")};

  ${Actions} > & {
    flex: 0 0 auto;
    margin-left: auto;
    min-width: ${({ theme }) => theme.size.control.md * 4}px;
  }

  &:disabled {
    border-color: ${({ theme }) => branch(theme, "border/default")};
    background: ${({ theme }) => branch(theme, "background/disabled")};
    color: ${({ theme }) => branch(theme, "text/disabled")};
  }
`;

/** A quiet pill, so the primary action keeps the weight. */
export const BackAction = styled.button`
  ${actionBase}
  flex: none;
  padding: 0 ${({ theme }) => theme.space.card.inset}px 0
    ${({ theme }) => theme.space.md}px;
  border-color: ${({ theme }) => branch(theme, "border/default")};
  background: none;
  color: ${({ theme }) => branch(theme, "text/primary")};

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      border-color: ${({ theme }) => branch(theme, "border/strong")};
    }
  }

  &:disabled {
    color: ${({ theme }) => branch(theme, "text/disabled")};
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Spinner = styled.span`
  width: ${({ theme }) => theme.size.icon.xs}px;
  height: ${({ theme }) => theme.size.icon.xs}px;
  border: ${({ theme }) => theme.stroke.selected}px solid currentColor;
  border-top-color: ${({ theme }) => branch(theme, "background/disabled")};
  border-radius: ${({ theme }) => theme.radius.full}px;
  animation: ${spin} ${({ theme }) => theme.motion.duration.slow}ms linear
    infinite;
`;

export const Reassurance = styled.p`
  ${typographyCSS("body.sm")}
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm}px;
  color: ${({ theme }) => branch(theme, "text/tertiary")};
`;
